/**
 * DataBest — Root error boundary.
 * Catches React render errors + surfaces a readable message instead of the
 * default "[object Object]" runtime overlay. Also intercepts window
 * "unhandledrejection" so async errors escaping useEffect chains produce
 * legible feedback instead of black-holing the UI.
 */
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const formatError = (err) => {
    if (!err) return "Unknown error";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || err.toString();
    if (typeof err === "object") {
        if (err.message) return err.message;
        if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
        try {
            return JSON.stringify(err, Object.getOwnPropertyNames(err));
        } catch (_) {
            return String(err);
        }
    }
    return String(err);
};

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
        this._onRejection = this._onRejection.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary]", formatError(error), info);
    }

    componentDidMount() {
        window.addEventListener("unhandledrejection", this._onRejection);
    }

    componentWillUnmount() {
        window.removeEventListener("unhandledrejection", this._onRejection);
    }

    _onRejection(event) {
        // Surface readable toast so users don't see "[object Object]".
        const msg = formatError(event.reason);
        try {
            toast.error(msg);
        } catch (_) {
            // toast may not be mounted yet on very early failures
        }
        // eslint-disable-next-line no-console
        console.warn("[unhandledrejection]", msg);
        // Prevent React 19 dev overlay from stringifying the object badly.
        event.preventDefault();
    }

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;
        const msg = formatError(error);
        return (
            <div
                className="flex min-h-[100dvh] items-center justify-center bg-slate-50 p-6"
                data-testid="error-boundary-view"
            >
                <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="h-5 w-5" />
                        <h2 className="font-heading text-lg font-bold">
                            Something went wrong
                        </h2>
                    </div>
                    <p className="mt-3 text-sm text-slate-700">{msg}</p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-blue-800"
                        data-testid="error-boundary-reload"
                    >
                        <RefreshCw className="h-4 w-4" /> Reload page
                    </button>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
